<script lang="ts">
import type { NavigationMenuRootProps } from 'reka-ui';
import type { NavigationMenuSlots } from '#ui/types';
import type { ArrayOrNested } from '#ui/types/utils';
import type { SidebarContentItemEmits, SidebarItem } from './ContentItem.vue';
import { isNullishOrEmpty } from '#layers/base/public';

export interface SidebarContentProps<T extends ArrayOrNested<SidebarItem> = ArrayOrNested<SidebarItem>> extends Pick<NavigationMenuRootProps, 'modelValue' | 'defaultValue' | 'delayDuration' | 'disableClickTrigger' | 'disableHoverTrigger' | 'skipDelayDuration' | 'disablePointerLeaveClose' | 'unmountOnHide'> {
	as?: any;
	collapsed?: boolean;
	editMode?: boolean;
	externalIcon?: string;
	items?: T;
	trailingIcon?: string;
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<SidebarItem>">
import { isArrayOfArray } from '@nuxt/ui/runtime/utils/index.js';
import { NavigationMenuList, NavigationMenuRoot, useForwardProps } from 'reka-ui';
import { computed } from 'vue';

const {
	delayDuration = 0,
	unmountOnHide = true,
	items,
	as,
	modelValue,
	defaultValue,
	skipDelayDuration,
	disableClickTrigger,
	disableHoverTrigger,
	disablePointerLeaveClose,
} = defineProps<SidebarContentProps<T>>();

defineEmits<SidebarContentItemEmits>();

defineSlots<Slots>();

type Slots = NavigationMenuSlots<T> & {
	'content-top': (props?: object) => void;
	'content-bottom': (props?: object) => void;
};

const rootProps = useForwardProps(computed(() => ({ as, modelValue, defaultValue, delayDuration, skipDelayDuration, disableClickTrigger, disableHoverTrigger, disablePointerLeaveClose, unmountOnHide }))) as NavigationMenuRootProps;

function isSidebarItemGroups(items: ArrayOrNested<SidebarItem>): items is SidebarItem[][] {
	return isArrayOfArray(items);
}

const lists = computed<SidebarItem[][]>(() => {
	if (isNullishOrEmpty(items)) return [];
	return isSidebarItemGroups(items) ? items : [items];
});
</script>

<template>
	<NavigationMenuRoot
		v-bind="rootProps"
		:data-collapsed="collapsed"
		class="space-y-4"
	>
		<template v-for="(list, listIndex) in lists" :key="`list-${listIndex}`">
			<hr v-if="collapsed && listIndex > 0" class="mx-1 border-default">
			<NavigationMenuList class="isolate min-w-0 space-y-px select-none">
				<DSidebarContentItem
					v-for="(item, index) in list"
					:key="`list-${listIndex}-${item.id}`"
					:collapsed
					:index
					:edit-mode
					:item
					@hide="(id: SidebarItem['id']) => $emit('hide', id)"
					@show="(id: SidebarItem['id']) => $emit('show', id)"
				/>
			</NavigationMenuList>
		</template>
	</NavigationMenuRoot>
</template>
